import { Mail, Handshake, MapPin, MessagesSquare, ListChecks, Tag } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useSEO } from "@/hooks/useSEO";

const steps = [
  {
    icon: MessagesSquare,
    title: "Tell us your plans",
    body: "Share your markets, brands, and expected volumes by email — raman@vowpin.com for sales, sam@vowpin.com for partnerships.",
  },
  {
    icon: ListChecks,
    title: "We curate your catalog",
    body: "You get a tailored catalogue with the brands, denominations, and regions that fit your program.",
  },
  {
    icon: Tag,
    title: "Best-offer pricing",
    body: "We reply within one business day with pricing matched to your volume — and your first delivery can run within days.",
  },
];

export default function Contact() {
  useSEO(
    "Contact Vowpin Global — Let's Grow Together",
    "Looking for a reliable global digital gift card partner? Contact Vowpin Global for sales enquiries and business partnerships across 120+ countries."
  );

  return (
    <main className="pt-24">
      <section className="relative overflow-hidden bg-white py-20 lg:py-28">
        <div className="relative mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-[1fr_1.1fr] lg:px-10">
          <div>
            <Reveal>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c98a00]">Contact</p>
              <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.08] tracking-tight text-[#111827] sm:text-5xl">
                Let's <span className="text-gradient-blue">Grow Together</span>
              </h1>
              <p className="mt-6 max-w-md text-base leading-relaxed text-[#64748b] md:text-lg">
                Looking for a reliable global digital gift card partner? Our team is ready to help
                your business expand into international digital rewards.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-12 space-y-4" data-testid="contact-direct-emails">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9aa3b5]">Reach us directly</p>
                <a
                  href="mailto:raman@vowpin.com"
                  data-testid="contact-email-raman"
                  className="glow-card flex items-center gap-4 rounded-[20px] px-6 py-5"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f4a900]/[0.12]">
                    <Mail className="h-5 w-5 text-[#c98a00]" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-[#9aa3b5]">Sales Enquiries</p>
                    <p className="mt-0.5 font-display text-base font-semibold text-[#111827]">raman@vowpin.com</p>
                  </div>
                </a>
                <a
                  href="mailto:sam@vowpin.com"
                  data-testid="contact-email-sam"
                  className="glow-card flex items-center gap-4 rounded-[20px] px-6 py-5"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0816a8]/[0.08]">
                    <Handshake className="h-5 w-5 text-[#0816a8]" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-[#9aa3b5]">Business Partnerships</p>
                    <p className="mt-0.5 font-display text-base font-semibold text-[#111827]">sam@vowpin.com</p>
                  </div>
                </a>
                <div
                  className="glow-card flex items-center gap-4 rounded-[20px] px-6 py-5"
                  data-testid="contact-address"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0816a8]/[0.08]">
                    <MapPin className="h-5 w-5 text-[#0816a8]" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-[#9aa3b5]">Registered Office</p>
                    <p className="mt-0.5 text-sm font-medium leading-relaxed text-[#374151]">
                      Building A1, Dubai Digital Park, Dubai Silicon Oasis, Dubai, United Arab Emirates
                    </p>
                  </div>
                </div>
                <div className="glow-card overflow-hidden rounded-[20px]" data-testid="contact-map">
                  <iframe
                    title="Vowpin Global — Dubai Silicon Oasis office map"
                    src="https://www.google.com/maps?q=Building%20A1%2C%20Dubai%20Digital%20Park%2C%20Dubai%20Silicon%20Oasis%2C%20Dubai&output=embed"
                    className="h-64 w-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="glow-card rounded-[24px] p-8 sm:p-10" data-testid="contact-next-steps">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#c98a00]">What happens next</p>
              <h2 className="mt-4 font-display text-2xl font-semibold text-[#111827]">
                Three steps to your first delivery
              </h2>
              <div className="mt-8 space-y-4">
                {steps.map((s, i) => (
                  <div
                    key={s.title}
                    className="flex items-start gap-5 rounded-2xl border border-[#e8ecf4] bg-[#f7f9fc] p-6"
                    data-testid={`contact-step-${i}`}
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#0816a8] shadow-[0_8px_20px_-6px_rgba(8,22,168,0.5)]">
                      <s.icon className="h-5 w-5 text-white" />
                    </span>
                    <div>
                      <p className="font-display text-base font-semibold text-[#111827]">
                        <span className="mr-2 text-[#c98a00]">{String(i + 1).padStart(2, "0")}</span>
                        {s.title}
                      </p>
                      <p className="mt-1.5 text-sm leading-relaxed text-[#64748b]">{s.body}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 rounded-2xl bg-[#0816a8]/[0.04] p-6">
                <p className="text-sm leading-relaxed text-[#64748b]">
                  Prefer to browse first? Explore the{" "}
                  <a href="/products" className="font-semibold text-[#0816a8] hover:underline">full catalog</a>{" "}
                  or the regional catalogues for{" "}
                  <a href="/regions/india" className="font-semibold text-[#0816a8] hover:underline">India</a>,{" "}
                  <a href="/regions/uae" className="font-semibold text-[#0816a8] hover:underline">UAE</a>,{" "}
                  <a href="/regions/ksa" className="font-semibold text-[#0816a8] hover:underline">KSA</a>, and{" "}
                  <a href="/regions/usa" className="font-semibold text-[#0816a8] hover:underline">USA</a>.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
